import type { PropsWithChildren } from 'react'
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useUnit } from 'effector-react'
import set from 'lodash/set'

import { $account } from '@/models/account.ts'
import { handleError } from '@/utils/error'
import { deepCopy } from '@/utils/object'

import { SalesEditorActionsDropdown } from '../actions-dropdown'
import { useEditorTemplates } from '../hooks/use-editor-templates'
import { mergeChangesWithTemplate } from '../utils'
import type {
  EditorValue,
  SalesEditorContextValue,
  SalesEditorTemplate,
} from './types'

export const SalesEditorContext = createContext<SalesEditorContextValue | null>(
  null,
)

export const SalesEditorProvider = ({ children }: PropsWithChildren) => {
  const account = useUnit($account)
  const [isEditing, setIsEditing] = useState(false)
  const [currentChanges, setCurrentChanges] = useState<
    SalesEditorTemplate['data']
  >({})
  const keydownHandlerRef = useRef<null | ((event: KeyboardEvent) => void)>(
    null,
  )

  const {
    templates,
    activeTemplate,
    createNewTemplate,
    updateTemplate,
    removeTemplate,
    changeActiveTemplate,
  } = useEditorTemplates()

  const setChanges = useCallback(
    (dateRange: string, key: string, value: EditorValue) => {
      setCurrentChanges((prevChanges) => {
        const newValue = deepCopy(prevChanges)

        set(newValue, `${dateRange}.${key}`, value)

        return newValue
      })
    },
    [setCurrentChanges],
  )

  const saveChanges = useCallback(() => {
    try {
      if (!activeTemplate) {
        throw new Error('No active template found')
      }

      const newTemplateData = mergeChangesWithTemplate(
        activeTemplate.data,
        currentChanges,
      )

      updateTemplate(activeTemplate.id, undefined, newTemplateData)
      setCurrentChanges({})
    } catch (error: Error | unknown) {
      handleError(error, 'An error occurred while saving the changes')
    }
  }, [activeTemplate, currentChanges, updateTemplate])

  const isEditingAllowed = !!account?.salesEditorAccess

  const handleShortcut = useCallback(
    (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'h') {
        setIsEditing((isEditing) => {
          if (isEditing) {
            saveChanges()
          }

          return !isEditing
        })
      }
    },
    [saveChanges],
  )

  useEffect(() => {
    if (!isEditingAllowed) return

    const cleanUp = () => {
      if (keydownHandlerRef.current) {
        window.removeEventListener('keydown', keydownHandlerRef.current)
      }
    }

    cleanUp()

    keydownHandlerRef.current = handleShortcut
    window.addEventListener('keydown', keydownHandlerRef.current)

    return cleanUp
  }, [handleShortcut, isEditingAllowed])

  const value: SalesEditorContextValue = useMemo(
    () => ({
      templates,
      activeTemplate,
      createNewTemplate,
      updateTemplate,
      removeTemplate,
      changeActiveTemplate,
      setChanges,
      isEditing,
      currentChanges,
      setCurrentChanges,
      saveChanges,
    }),
    [
      templates,
      activeTemplate,
      createNewTemplate,
      updateTemplate,
      removeTemplate,
      changeActiveTemplate,
      setChanges,
      isEditing,
      currentChanges,
      setCurrentChanges,
      saveChanges,
    ],
  )

  return (
    <SalesEditorContext.Provider value={value}>
      {children}

      {isEditing && <SalesEditorActionsDropdown />}
    </SalesEditorContext.Provider>
  )
}
